/**
 * UnifiedCartManager - Modern cart system with polymorphic references
 * 
 * Features:
 * - Zero data duplication through polymorphic references
 * - Full DiscountEngine integration (preserves SAVE20 coupon system)
 * - Full AddonServicesService integration (preserves 645-line enterprise system)
 * - Multi-currency support with precision handling
 * - Real-time calculations and validation
 */

import { realProfileService } from '@/services/RealProfileService';
import { modernCartService } from '@/services/ModernCartService';
import { discountEngine, type AppliedDiscount, type QuoteComponents } from '@/services/DiscountEngine';
import { addonServicesService } from '@/services/AddonServicesService';
import { QUERY_COLUMNS } from '@/lib/queryColumns';
import type { Database } from '@/integrations/supabase/types';

type CartItem = Database['public']['Tables']['cart_items']['Row'];
type CartSession = Database['public']['Tables']['cart_sessions']['Row'];

export interface CartItemInput {
  sourceType: 'quote' | 'product' | 'consolidation';
  sourceId: string;
  quantity: number;
  selectedAddonServices?: AddonServiceSelection[];
  options?: Record<string, any>; // Product variant options (size, color, etc.)
  variantId?: string; // Specific variant identifier if needed
}

export interface AddonServiceSelection {
  service_key: string;
  is_selected: boolean;
  calculated_amount: number;
  recommendation_score?: number;
}

export interface CartTotals {
  subtotal: number;
  discountAmount: number;
  addonAmount: number;
  taxAmount: number;
  totalAmount: number;
  currency: string;
  appliedDiscounts: AppliedDiscount[];
  appliedAddons: AddonServiceSelection[];
}

export interface DiscountContext {
  country: string;
  totalWeight?: number;
  paymentMethod?: string;
  city?: string;
}

export interface ValidationResult {
  valid: boolean;
  warnings: string[];
  errors: string[];
}

export class UnifiedCartManager {
  private static instance: UnifiedCartManager;
  
  public static getInstance(): UnifiedCartManager {
    if (!UnifiedCartManager.instance) {
      UnifiedCartManager.instance = new UnifiedCartManager();
    }
    return UnifiedCartManager.instance;
  }

  /**
   * Create new cart session (with enhanced session tracking) - using HybridCartService
   */
  async createCart(customerId: string): Promise<CartSession> {
    console.log('[CART-SESSION-MANAGER] Creating new cart for customer:', customerId);

    // Enhanced logging: Check if customer already has sessions using HybridCartService
    const existingSessions = await modernCartService.getCartSessions(customerId);

    console.log('[CART-SESSION-MANAGER] Existing sessions for customer:', {
      customerId,
      existingSessionCount: existingSessions?.length || 0,
      sessionIds: existingSessions?.map(s => s.id) || []
    });

    // Create cart session using HybridCartService
    const data = await modernCartService.createCartSession(customerId);

    console.log('[CART-SESSION-MANAGER] ✅ Cart created successfully:', {
      newSessionId: data.id,
      customerId,
      totalSessions: (existingSessions?.length || 0) + 1
    });

    // Return in expected format
    return {
      id: data.id,
      user_id: data.user_id,
      created_at: data.created_at,
      expires_at: data.expires_at || null,
      metadata: data.metadata || null
    } as CartSession;
  }

  /**
   * Add item to cart with polymorphic reference (ENHANCED: anti-fragmentation tracking) - using HybridCartService
   */
  async addItem(cartId: string, item: CartItemInput): Promise<void> {
    console.log('[CART-UNIFIED-ANTI-FRAG] Adding item to unified cart:', { cartId, item });

    // ANTI-FRAGMENTATION: Validate session exists using HybridCartService
    const session = await modernCartService.getCartSession(cartId);

    if (!session) {
      console.error('[CART-UNIFIED-ANTI-FRAG] Session validation failed: session not found');
      throw new Error(`Cart session ${cartId} not found or invalid`);
    }

    // Check for session fragmentation (multiple active sessions per user)
    const userSessions = await modernCartService.getCartSessions(session.user_id);

    if (userSessions && userSessions.length > 1) {
      console.warn('[CART-UNIFIED-ANTI-FRAG] ⚠️ FRAGMENTATION DETECTED:', {
        userId: session.user_id,
        activeSessionCount: userSessions.length,
        currentSessionId: cartId,
        allSessionIds: userSessions.map(s => s.id),
        recommendation: 'Consider consolidating sessions to prevent item loss'
      });
    }

    // Check existing items in this session for context using HybridCartService
    const existingItems = await modernCartService.getCartItems(cartId);

    console.log('[CART-UNIFIED-ANTI-FRAG] Session context before adding item:', {
      sessionId: cartId,
      existingItemCount: existingItems?.length || 0,
      existingItemTypes: existingItems?.map(i => i.source_type) || [],
      newItemType: item.sourceType,
      unifiedSessionStrategy: 'preserve_existing_items'
    });

    // Resolve source details to get pricing
    console.log('[quote-item-cart] addItem - About to resolve source details:', {
      sourceType: item.sourceType,
      sourceId: item.sourceId,
      timestamp: new Date().toISOString(),
      debugNote: 'This should call resolveSourceDetails and extract quote totals'
    });

    const sourceDetails = await this.resolveSourceDetails(item.sourceType, item.sourceId);

    console.log('[quote-item-cart] addItem - Source details resolved:', {
      sourceType: item.sourceType,
      sourceId: item.sourceId,
      resolvedDetails: sourceDetails,
      isQuoteWithZeroPrice: item.sourceType === 'quote' && sourceDetails.unitPrice === 0,
      timestamp: new Date().toISOString(),
      debugNote: sourceDetails.unitPrice === 0 ? 'PROBLEM: resolveSourceDetails returned 0 - this will cause zero totals' : 'SUCCESS: Valid price resolved'
    });

    // Create cart item using HybridCartService
    const cartItemData = {
      source_type: item.sourceType,
      source_id: item.sourceId,
      quantity: item.quantity,
      original_amount: sourceDetails.unitPrice || 0,
      currency: sourceDetails.currency || 'USD',
      selected_addon_services: item.selectedAddonServices || [],
      selected_options: item.options || {},
      catalog_variant_id: item.variantId
    };

    await modernCartService.addCartItem(cartId, cartItemData);

    console.log('[CART-UNIFIED-ANTI-FRAG] ✅ Item added to unified session successfully:', {
      sessionId: cartId,
      itemType: item.sourceType,
      itemId: item.sourceId,
      sessionIntegrity: 'maintained',
      antiFragmentationStatus: 'success'
    });
  }

  /**
   * Remove item from cart (ENHANCED: with database persistence verification) - using HybridCartService
   */
  async removeItem(cartId: string, itemId: string): Promise<void> {
    console.log('[CART-DELETE-DEBUG] Removing item using HybridCartService:', { cartId, itemId });

    // Use HybridCartService to remove the item
    await modernCartService.removeCartItem(itemId);

    console.log('[CART-DELETE-DEBUG] ✅ Item removed successfully using HybridCartService:', {
      cartId,
      itemId,
      deletionCompleted: true
    });
  }

  /**
   * Update item quantity - using HybridCartService
   */
  async updateQuantity(cartId: string, itemId: string, quantity: number): Promise<void> {
    console.log('[CART-MANAGER] Updating quantity:', { cartId, itemId, quantity });

    await modernCartService.updateCartItemQuantity(itemId, quantity);

    console.log('[CART-MANAGER] Quantity updated successfully');
  }

  /**
   * Apply discount using DiscountEngine (preserves SAVE20 system)
   */
  async applyDiscount(cartId: string, code: string, context: DiscountContext): Promise<AppliedDiscount> {
    console.log('[CART-MANAGER] Applying discount:', { cartId, code, context });

    // Get cart totals for discount calculation
    const totals = await this.calculateTotals(cartId);
    
    // Use existing DiscountEngine to validate and apply discount
    const quoteComponents: QuoteComponents = {
      total: totals.subtotal,
      items_subtotal: totals.subtotal,
      shipping_cost: 0, // Will be calculated in checkout
      customs_duty: 0,  // Will be calculated in checkout
      handling_fee: 0,  // Will be calculated in checkout
      local_delivery_fee: 0,
      local_tax_amount: 0,
      gateway_fee: 0,
      insurance_amount: 0
    };

    const validation = await discountEngine.validateCouponCode(
      code,
      context.country,
      quoteComponents,
      totals.currency
    );

    if (!validation.valid || !validation.discount) {
      throw new Error(validation.error || 'Discount code is not valid');
    }

    // Apply discount to cart items
    await this.updateCartDiscount(cartId, validation.discount);

    console.log('[CART-MANAGER] Discount applied successfully:', validation.discount);
    return validation.discount;
  }

  /**
   * Remove discount from cart
   */
  async removeDiscount(cartId: string, discountId: string): Promise<void> {
    console.log('[CART-MANAGER] Removing discount:', { cartId, discountId });

    const { error } = await supabase
      .from('cart_items')
      .update({ 
        discounted_amount: null 
      })
      .eq('session_id', cartId);

    if (error) {
      console.error('[CART-MANAGER] Error removing discount:', error);
      throw new Error(`Failed to remove discount: ${error.message}`);
    }

    console.log('[CART-MANAGER] Discount removed successfully');
  }

  /**
   * Get add-on service recommendations using AddonServicesService (preserves 645-line system)
   */
  async getRecommendedAddons(cartId: string, context: { country: string; customerTier?: string }): Promise<any> {
    console.log('[CART-MANAGER] Getting add-on recommendations:', { cartId, context });

    const totals = await this.calculateTotals(cartId);

    // Use existing AddonServicesService (645 lines of enterprise logic)
    const recommendations = await addonServicesService.getRecommendedServices({
      country_code: context.country,
      order_value: totals.subtotal,
      order_type: 'cart',
      customer_tier: context.customerTier || 'regular'
    }, totals.currency);

    console.log('[CART-MANAGER] Add-on recommendations retrieved:', recommendations.recommendations?.length || 0);
    return recommendations;
  }

  /**
   * Apply add-on service to cart
   */
  async applyAddonService(cartId: string, serviceKey: string, amount: number): Promise<void> {
    console.log('[CART-MANAGER] Applying add-on service:', { cartId, serviceKey, amount });

    // Get cart items and apply addon to appropriate items
    const { data: cartItems, error } = await supabase
      .from('cart_items')
      .select('*')
      .eq('session_id', cartId);

    if (error || !cartItems) {
      throw new Error('Failed to load cart items');
    }

    // Update items with addon service
    for (const item of cartItems) {
      const currentOptions = item.selected_options as any || {};
      const currentAddons = currentOptions.addon_services || [];
      const updatedAddons = [...currentAddons, {
        service_key: serviceKey,
        is_selected: true,
        calculated_amount: amount
      }];

      await supabase
        .from('cart_items')
        .update({ 
          selected_options: {
            ...currentOptions,
            addon_services: updatedAddons
          },
          addon_cost: (item.addon_cost || 0) + amount
        })
        .eq('id', item.id);
    }

    console.log('[CART-MANAGER] Add-on service applied successfully');
  }

  /**
   * Remove add-on service from cart
   */
  async removeAddonService(cartId: string, serviceKey: string): Promise<void> {
    console.log('[CART-MANAGER] Removing add-on service:', { cartId, serviceKey });

    const { data: cartItems, error } = await supabase
      .from('cart_items')
      .select('*')
      .eq('session_id', cartId);

    if (error || !cartItems) {
      throw new Error('Failed to load cart items');
    }

    // Remove addon from items
    for (const item of cartItems) {
      const currentOptions = item.selected_options as any || {};
      const currentAddons = currentOptions.addon_services || [];
      const updatedAddons = currentAddons.filter(addon => addon.service_key !== serviceKey);
      const removedAddon = currentAddons.find(addon => addon.service_key === serviceKey);
      const newAddonCost = (item.addon_cost || 0) - (removedAddon?.calculated_amount || 0);

      await supabase
        .from('cart_items')
        .update({ 
          selected_options: {
            ...currentOptions,
            addon_services: updatedAddons
          },
          addon_cost: Math.max(0, newAddonCost)
        })
        .eq('id', item.id);
    }

    console.log('[CART-MANAGER] Add-on service removed successfully');
  }

  /**
   * 🔥 FIX: Optimized cart totals calculation - using HybridCartService
   */
  async calculateTotals(cartId: string): Promise<CartTotals> {
    console.log('[cart-debug] UnifiedCartManager - Starting calculateTotals:', {
      cartId,
      timestamp: new Date().toISOString()
    });

    // Get cart items using HybridCartService
    const cartItems = await modernCartService.getCartItems(cartId);

    if (!cartItems) {
      console.error('[cart-debug] UnifiedCartManager - Failed to load cart items');
      throw new Error('Failed to load cart items');
    }

    console.log('[cart-debug] UnifiedCartManager - Loaded cart items:', {
      itemCount: cartItems.length,
      itemIds: cartItems.map(item => item.id),
      timestamp: new Date().toISOString()
    });

    // 🔥 DEBUG: Specific tracking for quote items in calculateTotals
    const quoteItems = cartItems.filter(item => item.source_type === 'quote');
    if (quoteItems.length > 0) {
      console.log('[quote-item-cart] UnifiedCartManager.calculateTotals - Quote items detected:', {
        cartId,
        quoteItemCount: quoteItems.length,
        totalItemCount: cartItems.length,
        quoteItemsRawData: quoteItems.map(item => ({
          id: item.id,
          source_id: item.source_id,
          original_amount: item.original_amount,
          discounted_amount: item.discounted_amount,
          addon_cost: item.addon_cost,
          quantity: item.quantity,
          currency: item.currency,
          expectedItemTotal: (item.original_amount || 0) * (item.quantity || 1),
          ISSUE: 'This original_amount might be stale - need to fetch fresh quote data!'
        })),
        timestamp: new Date().toISOString(),
        debugNote: 'Raw quote items data at start of calculateTotals before any processing'
      });
    }

    // 🔥 FIX: Get cart session using HybridCartService
    const cartSession = await modernCartService.getCartSession(cartId);

    let displayCurrency = 'USD';
    if (cartSession?.user_id) {
      const userProfile = await realProfileService.getProfile(cartSession.user_id);
      displayCurrency = userProfile?.preferred_display_currency || 'USD';
    }

    console.log('[cart-debug] UnifiedCartManager - Currency setup:', {
      userId: cartSession?.user_id,
      displayCurrency,
      timestamp: new Date().toISOString()
    });

    // 🔥 FIX: Simplified calculation without excessive logging
    let subtotal = 0;
    let discountAmount = 0;
    let addonAmount = 0;
    const appliedDiscounts: AppliedDiscount[] = [];
    const appliedAddons: AddonServiceSelection[] = [];

    const { convertAmountWithSessionLock } = await import('@/services/SessionCurrencyService');

    // Process items with dynamic currency lookup (no stored currency)
    for (const item of cartItems) {
      // 🔥 DEBUG: Track quote items specifically in processing loop
      const isQuoteItem = item.source_type === 'quote';
      
      // 🔥 SIMPLIFIED FIX: For quote items, directly grab totals from quotes_v2.calculation_data
      let originalAmount = item.original_amount || 0;
      let itemCurrency = await this.getCurrencyFromSource(item.source_type!, item.source_id!);
      
      if (isQuoteItem) {
        console.log('[quote-item-cart] calculateTotals - Quote item detected - fetching direct from quotes_v2:', {
          cartId,
          itemId: item.id,
          sourceId: item.source_id,
          storedAmount: item.original_amount,
          debugNote: 'Will fetch total directly from quotes_v2.calculation_data.data.totals.total'
        });
        
        try {
          // 🔥 DIRECT APPROACH: Get the total directly from quotes_v2.calculation_data
          const { data: quote } = await supabase
            .from('quotes_v2')
            .select('calculation_data, origin_country')
            .eq('id', item.source_id)
            .single();
          
          if (quote?.calculation_data?.data?.totals?.total) {
            const directTotal = quote.calculation_data.data.totals.total;
            const directCurrency = quote.calculation_data.data.currency;
            
            // 🔥 DIRECT FIX: Use the direct total without complex currency conversion (same as quote pages)
            originalAmount = directTotal;
            itemCurrency = 'INR'; // Origin country currency - direct, simple approach
            
            console.log('[quote-item-cart] calculateTotals - DIRECT APPROACH (same as quote pages fix):', {
              cartId,
              itemId: item.id,
              sourceId: item.source_id,
              directTotal,
              approach: 'no_complex_conversion',
              reasoning: 'calculation_data.total already in correct format - same fix as quote pages',
              debugNote: 'Using direct amount without conversion to prevent ₹3,709 error'
            });
            
            console.log('[quote-item-cart] calculateTotals - DIRECT DATA SUCCESS:', {
              cartId,
              itemId: item.id,
              sourceId: item.source_id,
              storedAmount: item.original_amount,
              directTotal,
              directCurrency,
              finalAmount: originalAmount,
              finalCurrency: itemCurrency,
              usingDirectData: true,
              debugNote: 'Successfully grabbed and converted total from calculation_data!'
            });
          } else {
            console.warn('[quote-item-cart] calculateTotals - DIRECT DATA MISSING:', {
              cartId,
              itemId: item.id,
              sourceId: item.source_id,
              hasCalculationData: !!quote?.calculation_data,
              hasDataSection: !!quote?.calculation_data?.data,
              hasTotals: !!quote?.calculation_data?.data?.totals,
              hasTotal: !!quote?.calculation_data?.data?.totals?.total,
              debugNote: 'Quote missing calculation_data.data.totals.total - will use stored/fallback amount'
            });
          }
        } catch (error) {
          console.error('[quote-item-cart] calculateTotals - DIRECT DATA ERROR:', {
            cartId,
            itemId: item.id,
            sourceId: item.source_id,
            error: error instanceof Error ? error.message : error,
            debugNote: 'Failed to fetch quote calculation_data directly'
          });
        }
      }
      const discountedAmount = item.discounted_amount || originalAmount;
      const addonCost = item.addon_cost || 0;
      const quantity = item.quantity || 1;

      if (isQuoteItem) {
        console.log('[quote-item-cart] UnifiedCartManager.calculateTotals - Processing quote item:', {
          cartId,
          itemId: item.id,
          sourceId: item.source_id,
          rawValues: {
            original_amount: item.original_amount,
            discounted_amount: item.discounted_amount,
            addon_cost: item.addon_cost,
            quantity: item.quantity,
            stored_currency: item.currency
          },
          processedValues: {
            itemCurrency,
            originalAmount,
            discountedAmount,
            addonCost,
            quantity,
            expectedItemContribution: originalAmount * quantity
          },
          directDataUsed: originalAmount !== (item.original_amount || 0),
          dataSource: originalAmount !== (item.original_amount || 0) ? 'quotes_v2.calculation_data.data.totals.total' : 'stored_cart_item_amount',
          timestamp: new Date().toISOString(),
          debugNote: originalAmount !== (item.original_amount || 0) ? 
            'SUCCESS: Using direct calculation_data total instead of stored 0 amount' : 
            'Using stored cart amount (may be 0 if not fixed)'
        });
      }

      console.log('[cart-debug] UnifiedCartManager - Processing item:', {
        itemId: item.id,
        sourceType: item.source_type,
        itemCurrency,
        originalAmount,
        discountedAmount,
        addonCost,
        quantity,
        timestamp: new Date().toISOString()
      });

      // DIRECT FIX: For quote items, skip complex conversion (same as quote pages fix)
      let convertedOriginalAmount: number;
      let convertedDiscountedAmount: number;
      let convertedAddonCost: number;
      
      if (isQuoteItem) {
        // DIRECT: For quotes, use amounts directly without conversion
        convertedOriginalAmount = originalAmount;
        convertedDiscountedAmount = discountedAmount;
        convertedAddonCost = addonCost;
        
        console.log('[quote-item-cart] UnifiedCartManager - DIRECT approach for quote (no conversion):', {
          cartId,
          itemId: item.id,
          directAmounts: {
            originalAmount,
            discountedAmount,
            addonCost
          },
          approach: 'same_as_quote_pages_fix',
          reasoning: 'Skip conversion to prevent ₹3,709 error'
        });
      } else {
        // PRESERVE: Complex conversion for products/consolidation items
        const needsConversion = itemCurrency !== displayCurrency;
        
        convertedOriginalAmount = needsConversion 
          ? await convertAmountWithSessionLock(originalAmount, itemCurrency, displayCurrency)
          : originalAmount;
        
        convertedDiscountedAmount = needsConversion
          ? await convertAmountWithSessionLock(discountedAmount, itemCurrency, displayCurrency)
          : discountedAmount;
        
        convertedAddonCost = needsConversion
          ? await convertAmountWithSessionLock(addonCost, itemCurrency, displayCurrency)
          : addonCost;
      }

      // Calculate totals
      const itemSubtotal = convertedOriginalAmount * quantity;
      const itemDiscount = (convertedOriginalAmount - convertedDiscountedAmount) * quantity;
      const itemAddonCost = convertedAddonCost * quantity;
      
      subtotal += itemSubtotal;
      discountAmount += itemDiscount;
      addonAmount += itemAddonCost;

      // 🔥 DEBUG: Track quote item contribution to final totals
      if (isQuoteItem) {
        console.log('[quote-item-cart] UnifiedCartManager.calculateTotals - Quote item totals contribution:', {
          cartId,
          itemId: item.id,
          sourceId: item.source_id,
          currencyConversion: {
            itemCurrency,
            displayCurrency,
            needsConversion: itemCurrency !== displayCurrency,
            originalAmount,
            convertedOriginalAmount,
            conversionRatio: originalAmount > 0 ? (convertedOriginalAmount / originalAmount).toFixed(4) : '0'
          },
          itemCalculations: {
            itemSubtotal,
            itemDiscount,
            itemAddonCost,
            itemTotalContribution: itemSubtotal - itemDiscount + itemAddonCost
          },
          runningTotalsAfterThisItem: {
            subtotal,
            discountAmount,
            addonAmount,
            currentTotal: subtotal - discountAmount + addonAmount
          },
          timestamp: new Date().toISOString(),
          debugNote: 'Individual quote item contribution to cart totals after currency conversion'
        });
      }

      console.log('[cart-debug] UnifiedCartManager - Item calculations:', {
        itemId: item.id,
        conversions: {
          needsConversion: itemCurrency !== displayCurrency,
          originalAmount,
          convertedOriginalAmount,
          convertedDiscountedAmount,
          convertedAddonCost
        },
        calculations: {
          quantity,
          itemSubtotal,
          itemDiscount,
          itemAddonCost
        },
        runningTotals: {
          subtotal,
          discountAmount,
          addonAmount
        },
        // 🔥 CRITICAL: Check for 86x currency conversion issue
        conversionRatios: {
          originalToConverted: convertedOriginalAmount / originalAmount,
          expectedSingleItemDisplay: `${originalAmount} ${itemCurrency}`,
          actualConvertedDisplay: `${convertedOriginalAmount} ${displayCurrency}`,
          suspiciousMultiplier: convertedOriginalAmount > originalAmount ? (convertedOriginalAmount / originalAmount).toFixed(2) + 'x' : 'normal',
          potentialDoubleConversion: convertedOriginalAmount > originalAmount * 50 ? '🚨 SUSPICIOUS: Potential double/multiple conversion!' : 'ok'
        },
        timestamp: new Date().toISOString()
      });

      // Collect addons (discounts are handled separately)
      const itemOptions = item.selected_options as any || {};
      if (itemOptions.addon_services) {
        appliedAddons.push(...(itemOptions.addon_services as any[]));
      }
    }

    const finalTotals = {
      subtotal,
      discountAmount,
      addonAmount,
      taxAmount: 0,
      totalAmount: subtotal - discountAmount + addonAmount,
      currency: displayCurrency,
      appliedDiscounts,
      appliedAddons
    };

    console.log('[cart-debug] UnifiedCartManager - Final calculation result:', {
      cartId,
      displayCurrency,
      itemsProcessed: cartItems.length,
      finalTotals,
      calculation: `${subtotal} - ${discountAmount} + ${addonAmount} = ${finalTotals.totalAmount}`,
      timestamp: new Date().toISOString()
    });

    // 🔥 DEBUG: Final quote items summary for the zero totals investigation
    if (quoteItems.length > 0) {
      let quoteItemsExpectedTotal = 0;
      quoteItems.forEach(item => {
        quoteItemsExpectedTotal += (item.original_amount || 0) * (item.quantity || 1);
      });

      console.log('[quote-item-cart] UnifiedCartManager.calculateTotals - FINAL QUOTE ITEMS SUMMARY:', {
        cartId,
        quoteItemCount: quoteItems.length,
        totalItemsInCart: cartItems.length,
        finalTotalsReturned: finalTotals,
        quoteAnalysis: {
          expectedRawQuoteTotal: quoteItemsExpectedTotal,
          calculatedSubtotal: subtotal,
          calculatedTotalAmount: finalTotals.totalAmount,
          isZeroTotalIssue: finalTotals.totalAmount === 0 && quoteItemsExpectedTotal > 0,
          possibleCause: finalTotals.totalAmount === 0 && quoteItemsExpectedTotal > 0 
            ? 'Currency conversion or calculation error causing zero totals despite non-zero quote amounts'
            : finalTotals.totalAmount > 0 
            ? 'Calculation appears successful' 
            : 'No quote amounts to calculate'
        },
        detailedBreakdown: {
          subtotal: `${subtotal} ${displayCurrency}`,
          discountAmount: `${discountAmount} ${displayCurrency}`,
          addonAmount: `${addonAmount} ${displayCurrency}`,
          finalTotal: `${finalTotals.totalAmount} ${displayCurrency}`
        },
        timestamp: new Date().toISOString(),
        debugNote: 'This is the final result being returned to useModernCart and then to UI components - check if this matches what displays in CartOrderSummary'
      });
    }

    // 🔥 CRITICAL: Calculate expected totals from raw item data to identify 86x discrepancy source
    let expectedRawSubtotal = 0;
    for (const item of cartItems) {
      const itemTotal = (item.original_amount || 0) * (item.quantity || 1);
      expectedRawSubtotal += itemTotal;
    }
    
    console.log('[cart-debug] UnifiedCartManager - 86x DISCREPANCY ANALYSIS:', {
      cartId,
      backend_calculated_subtotal: subtotal,
      expected_raw_subtotal: expectedRawSubtotal,
      discrepancy_ratio: subtotal > 0 ? (subtotal / expectedRawSubtotal).toFixed(2) + 'x' : '0x',
      discrepancy_amount: Math.abs(subtotal - expectedRawSubtotal),
      is_86x_issue: Math.abs((subtotal / expectedRawSubtotal) - 86) < 5 ? '🚨 YES - This is the 86x issue!' : 'No',
      individual_item_amounts: cartItems.map(item => ({
        id: item.id,
        original_amount: item.original_amount,
        currency: item.currency,
        quantity: item.quantity,
        item_total: (item.original_amount || 0) * (item.quantity || 1)
      })),
      backend_conversion_results: `Subtotal: ${subtotal} ${displayCurrency}`,
      potential_causes: [
        'Currency conversion multiplying instead of converting',
        'Double/triple conversion happening',
        'Incorrect exchange rate (86x rate)',
        'Math error in quantity or price calculation'
      ],
      timestamp: new Date().toISOString()
    });

    return finalTotals;
  }

  /**
   * Validate cart before checkout
   */
  async validateCart(cartId: string): Promise<ValidationResult> {
    console.log('[CART-MANAGER] Validating cart:', cartId);

    const warnings: string[] = [];
    const errors: string[] = [];

    try {
      const { data: cartItems, error } = await supabase
        .from('cart_items')
        .select('*')
        .eq('session_id', cartId);

      if (error) {
        errors.push('Failed to load cart items');
        return { valid: false, warnings, errors };
      }

      if (!cartItems || cartItems.length === 0) {
        errors.push('Cart is empty');
        return { valid: false, warnings, errors };
      }

      // Validate each item's source still exists
      for (const item of cartItems) {
        const sourceExists = await this.validateSourceExists(item.source_type!, item.source_id!);
        if (!sourceExists) {
          errors.push(`Item source no longer available: ${item.source_type}:${item.source_id}`);
        }
      }

      // Validate totals
      const totals = await this.calculateTotals(cartId);
      if (totals.totalAmount < 0) {
        errors.push('Invalid cart total amount');
      }

      if (totals.totalAmount > 0 && totals.totalAmount < 1) {
        warnings.push('Cart total is very low, verify pricing');
      }

    } catch (error) {
      errors.push(`Validation error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    const valid = errors.length === 0;
    console.log('[CART-MANAGER] Cart validation result:', { valid, warnings: warnings.length, errors: errors.length });
    
    return { valid, warnings, errors };
  }

  /**
   * 🔥 FIX: Optimized enriched cart items with batched queries - using HybridCartService
   */
  async getEnrichedCartItems(cartId: string): Promise<any[]> {
    // Get cart items using HybridCartService
    const cartItems = await modernCartService.getCartItems(cartId);

    if (!cartItems) {
      throw new Error('Failed to load cart items');
    }
    
    // Debug raw database items for variant data
    console.log('[cart-variants] DEBUG getEnrichedCartItems - Raw database items:', {
      cartId,
      itemCount: cartItems.length,
      items: cartItems.map(item => ({
        id: item.id,
        source_type: item.source_type,
        selected_options: item.selected_options,
        catalog_variant_id: item.catalog_variant_id,
        hasSelectedOptions: !!item.selected_options,
        selectedOptionsKeys: item.selected_options ? Object.keys(item.selected_options) : null
      }))
    });

    // 🔥 FIX: Batch queries by source type to reduce database calls
    const quoteIds = cartItems.filter(item => item.source_type === 'quote').map(item => item.source_id);
    const productIds = cartItems.filter(item => item.source_type === 'product').map(item => item.source_id);
    const consolidationIds = cartItems.filter(item => item.source_type === 'consolidation').map(item => item.source_id);

    // Batch fetch all data in parallel
    const [quotesData, productsData, consolidationsData] = await Promise.all([
      quoteIds.length > 0 ? supabase.from('quotes_v2').select(QUERY_COLUMNS.QUOTE_DETAIL).in('id', quoteIds) : { data: [] },
      productIds.length > 0 ? this.fetchProductsFromAWS(productIds) : { data: [] },
      consolidationIds.length > 0 ? supabase.from('consolidation_requests').select('id, package_ids, consolidated_weight_kg, status, tracking_number').in('id', consolidationIds) : { data: [] }
    ]);

    // Create lookup maps for fast access
    const quotesMap = new Map((quotesData.data || []).map(q => [q.id, q]));
    const productsMap = new Map((productsData.data || []).map(p => [p.id, p]));
    const consolidationsMap = new Map((consolidationsData.data || []).map(c => [c.id, c]));

    // Enrich items using cached data
    const enrichedItems = cartItems.map(item => {
      try {
        const displayMetadata = this.buildDisplayMetadataFromCache(
          item.source_type!, 
          item.source_id!, 
          quotesMap, 
          productsMap, 
          consolidationsMap
        );
        
        return {
          ...item,
          displayMetadata,
          sourceType: item.source_type,
          sourceId: item.source_id
        };
      } catch (error) {
        // Fallback for failed enrichment
        return {
          ...item,
          displayMetadata: {
            displayName: `${item.source_type} #${item.source_id?.slice(0, 8)}`,
            productUrl: '#',
            productImage: null,
            brand: null,
            sourceType: item.source_type
          },
          sourceType: item.source_type,
          sourceId: item.source_id
        };
      }
    });

    return enrichedItems;
  }

  /**
   * 🔥 NEW: Build display metadata from cached data (performance optimization)
   */
  private buildDisplayMetadataFromCache(
    sourceType: string, 
    sourceId: string, 
    quotesMap: Map<string, any>, 
    productsMap: Map<string, any>, 
    consolidationsMap: Map<string, any>
  ) {
    switch (sourceType) {
      case 'product': {
        const product = productsMap.get(sourceId);
        if (!product) throw new Error(`Product ${sourceId} not found in cache`);
        
        return {
          displayName: this.formatDisplayName(product.title, 'product', sourceId),
          productUrl: `/products/${sourceId}`,
          productImage: product.featured_image,
          brand: product.brand || this.getSmartBrandFallback('product', 'Catalog', product.origin_currency),
          sourceType: 'product',
          routeInfo: {
            marketplace: 'Catalog',
            originCountry: product.origin_currency || 'USD'
          }
        };
      }

      case 'quote': {
        const quote = quotesMap.get(sourceId);
        if (!quote) throw new Error(`Quote ${sourceId} not found in cache`);

        const items = quote.items as any[] || [];
        const primaryItem = items[0];
        const itemCount = items.length;
        
        let displayName: string;
        if (itemCount === 1) {
          displayName = this.formatDisplayName(primaryItem?.name || primaryItem?.product_name, 'quote', sourceId);
        } else {
          const primaryName = primaryItem?.name || primaryItem?.product_name || 'Items';
          displayName = `${this.formatDisplayName(primaryName, 'quote', sourceId)} (+${itemCount - 1} More)`;
        }
        
        return {
          displayName,
          productUrl: `/dashboard/quotes/${sourceId}`,
          productImage: primaryItem?.product_image || null,
          brand: this.getSmartBrandFallback('quote'),
          sourceType: 'quote',
          routeInfo: {
            originCountry: quote.origin_country,
            destCountry: quote.destination_country
          }
        };
      }

      case 'consolidation': {
        const consolidation = consolidationsMap.get(sourceId);
        if (!consolidation) throw new Error(`Consolidation ${sourceId} not found in cache`);

        const packageIds = consolidation.package_ids as string[] || [];
        const packageCount = packageIds.length || 1;
        
        return {
          displayName: this.formatDisplayName(
            `Package Consolidation - ${packageCount} packages`, 
            'consolidation', 
            sourceId
          ),
          productUrl: `/dashboard/my-consolidations`,
          productImage: null,
          brand: this.getSmartBrandFallback('consolidation'),
          sourceType: 'consolidation',
          routeInfo: {
            originCountry: 'US',
            destCountry: 'varies',
            packageCount,
            weight: consolidation.consolidated_weight_kg ? `${consolidation.consolidated_weight_kg} kg` : 'N/A'
          }
        };
      }

      default:
        throw new Error(`Unknown source type: ${sourceType}`);
    }
  }

  /**
   * Private helper: Format display names with proper capitalization - ENHANCED for real product names
   */
  private formatDisplayName(name: string, sourceType: string, sourceId: string): string {
    if (!name || name.trim() === '') {
      // ONLY use fallback if absolutely no data available
      const shortId = sourceId.slice(0, 8).toUpperCase();
      console.warn('[DISPLAY-NAME-FALLBACK] Using fallback name for missing data:', { sourceType, sourceId });
      switch (sourceType) {
        case 'product': return `Product #${shortId}`;
        case 'consolidation': return `Consolidation #${shortId}`;  
        case 'quote': return `Quote #${shortId}`;
        default: return `Item #${shortId}`;
      }
    }
    
    // If we have actual name data, use it as-is (don't force title case on product names)
    // Many product names have specific capitalization (iPhone, MacBook, etc.)
    return name.trim();
  }

  /**
   * Private helper: Get smart brand fallback based on source type
   */
  private getSmartBrandFallback(sourceType: string, marketplace?: string, originCountry?: string): string {
    switch (sourceType) {
      case 'product': 
        return marketplace ? `${marketplace} Seller` : `${originCountry || 'Unknown'} Brand`;
      case 'consolidation':
        return 'iWishBag Consolidation';
      case 'quote':
        return 'Quote Service';
      default:
        return 'iWishBag';
    }
  }

  /**
   * Private helper: Resolve display metadata for cart items (ENHANCED: proper formatting)
   */
  private async resolveDisplayMetadata(sourceType: string, sourceId: string): Promise<{
    displayName: string;
    productUrl: string;
    productImage: string | null;
    brand: string | null;
    sourceType: string;
    routeInfo?: {
      originCountry?: string;
      destCountry?: string;
      marketplace?: string;
    };
  }> {
    console.log('[CART-DISPLAY-METADATA-ENHANCED] Resolving display metadata:', { sourceType, sourceId });

    switch (sourceType) {
      case 'product': {
        // Use AWS catalog service for product data
        const { awsCatalogService } = await import('@/services/AWSCatalogService');
        const result = await awsCatalogService.getProducts({}, { limit: 200 });

        if (!result.success || !result.data) {
          console.error('[CART-DISPLAY-METADATA-ENHANCED] AWS catalog fetch error:', { sourceId, result });
          throw new Error(`Product catalog fetch failed from AWS`);
        }

        const product = result.data.find(p => p.id === sourceId);

        if (!product) {
          throw new Error(`Product ${sourceId} not found in AWS catalog`);
        }
        
        // Use enhanced formatting
        const displayName = this.formatDisplayName(product.title, 'product', sourceId);
        const smartBrand = product.brand || this.getSmartBrandFallback('product', 'Catalog', product.origin_currency);
        
        console.log('[CART-DISPLAY-METADATA-ENHANCED] Product resolved:', {
          originalTitle: product.title,
          formattedDisplayName: displayName,
          originalBrand: product.brand,
          smartBrand,
          sourceId
        });
        
        return {
          displayName,
          productUrl: `/products/${sourceId}`,
          productImage: product.featured_image,
          brand: smartBrand,
          sourceType: 'product',
          routeInfo: {
            marketplace: 'Catalog',
            originCountry: product.origin_currency || 'USD'
          }
        };
      }

      case 'quote': {
        const { data: quote, error } = await supabase
          .from('quotes_v2')
          .select(`
            id, 
            calculation_data,
            items,
            origin_country,
            destination_country
          `)
          .eq('id', sourceId)
          .single();
        
        if (error) {
          console.error('[CART-DISPLAY-METADATA-ENHANCED] Quote fetch error:', { sourceId, error });
          throw new Error(`Quote ${sourceId} fetch failed: ${error.message}`);
        }
        
        if (!quote) {
          throw new Error(`Quote ${sourceId} not found`);
        }

        // Get primary item for display name with enhanced formatting
        const items = quote.items as any[] || [];
        const primaryItem = items[0];
        const itemCount = items.length;
        
        let displayName: string;
        if (itemCount === 1) {
          // For single item quotes, use the actual item name
          displayName = this.formatDisplayName(primaryItem?.name || primaryItem?.product_name, 'quote', sourceId);
        } else {
          // For multi-item quotes, show primary item + count
          const primaryName = primaryItem?.name || primaryItem?.product_name || 'Items';
          displayName = `${this.formatDisplayName(primaryName, 'quote', sourceId)} (+${itemCount - 1} More)`;
        }
        
        const smartBrand = this.getSmartBrandFallback('quote');
        
        console.log('[CART-DISPLAY-METADATA-ENHANCED] Quote resolved:', {
          itemCount,
          primaryItemName: primaryItem?.product_name,
          formattedDisplayName: displayName,
          smartBrand
        });
        
        return {
          displayName,
          productUrl: `/dashboard/quotes/${sourceId}`,
          productImage: primaryItem?.product_image || null,
          brand: smartBrand,
          sourceType: 'quote',
          routeInfo: {
            originCountry: quote.origin_country,
            destCountry: quote.destination_country
          }
        };
      }

      case 'consolidation': {
        const { data: consolidation, error } = await supabase
          .from('consolidation_requests')
          .select(`
            id,
            package_ids,
            consolidated_weight_kg,
            status,
            estimated_savings,
            tracking_number
          `)
          .eq('id', sourceId)
          .single();
        
        if (error) {
          console.error('[CART-DISPLAY-METADATA-ENHANCED] Consolidation fetch error:', { sourceId, error });
          throw new Error(`Consolidation ${sourceId} fetch failed: ${error.message}`);
        }
        
        if (!consolidation) {
          throw new Error(`Consolidation ${sourceId} not found`);
        }

        const packageIds = consolidation.package_ids as string[] || [];
        const packageCount = packageIds.length || 1;
        const trackingNumber = consolidation.tracking_number;
        const weight = consolidation.consolidated_weight_kg;
        
        // Enhanced consolidation display name
        const displayName = this.formatDisplayName(
          `Package Consolidation - ${packageCount} packages`, 
          'consolidation', 
          sourceId
        );
        
        const smartBrand = this.getSmartBrandFallback('consolidation');
        
        console.log('[CART-DISPLAY-METADATA-ENHANCED] Consolidation resolved:', {
          packageCount,
          trackingNumber,
          weight,
          formattedDisplayName: displayName,
          smartBrand
        });
        
        return {
          displayName,
          productUrl: `/dashboard/my-consolidations`,
          productImage: null,
          brand: smartBrand,
          sourceType: 'consolidation',
          routeInfo: {
            originCountry: 'US', // Consolidations are from US warehouse
            destCountry: 'varies', // Destination varies by customer
            packageCount,
            weight: weight ? `${weight} kg` : 'N/A'
          }
        };
      }

      default:
        throw new Error(`Unknown source type: ${sourceType}`);
    }
  }

  /**
   * Get currency dynamically from source tables (replaces stored cart currency)
   */
  private async getCurrencyFromSource(sourceType: string, sourceId: string): Promise<string> {
    console.log('[CART-CURRENCY-DYNAMIC] Getting currency from source:', { sourceType, sourceId });
    
    switch (sourceType) {
      case 'quote': {
        const { data: quote } = await supabase
          .from('quotes_v2')
          .select('id, origin_country')
          .eq('id', sourceId)
          .single();
        
        if (!quote) {
          console.warn('[CART-CURRENCY-DYNAMIC] Quote not found, defaulting to USD:', sourceId);
          return 'USD';
        }
        
        const { getOriginCurrency } = await import('@/utils/originCurrency');
        const currency = getOriginCurrency(quote.origin_country || 'US');
        
        console.log('[CART-CURRENCY-DYNAMIC] Quote currency resolved:', {
          quoteId: sourceId,
          originCountry: quote.origin_country,
          currency
        });
        
        return currency;
      }
      
      case 'product': {
        // Use AWS catalog service for product currency
        const { awsCatalogService } = await import('@/services/AWSCatalogService');
        const result = await awsCatalogService.getProducts({}, { limit: 200 });

        const product = result.success && result.data
          ? result.data.find(p => p.id === sourceId)
          : null;

        const currency = product?.origin_currency || 'USD';
        console.log('[CART-CURRENCY-DYNAMIC] Product currency resolved:', {
          productId: sourceId,
          currency
        });

        return currency;
      }
      
      case 'consolidation': {
        // Consolidation shipping costs are typically in USD for international shipping
        console.log('[CART-CURRENCY-DYNAMIC] Consolidation currency defaulted to USD:', sourceId);
        return 'USD';
      }
      
      default:
        console.warn('[CART-CURRENCY-DYNAMIC] Unknown source type, defaulting to USD:', sourceType);
        return 'USD';
    }
  }

  /**
   * Private helper: Resolve source details (polymorphic resolution)
   */
  private async resolveSourceDetails(sourceType: string, sourceId: string): Promise<{ unitPrice: number; currency: string }> {
    console.log('[quote-item-cart] resolveSourceDetails - ENTRY POINT:', { 
      sourceType, 
      sourceId,
      timestamp: new Date().toISOString(),
      debugNote: 'Starting quote total resolution - this is critical for cart pricing'
    });

    switch (sourceType) {
      case 'quote': {
        // 🔥 KEY FIX: Get full quote data for calculateQuoteTotal function
        const { data: quote } = await supabase
          .from('quotes_v2')
          .select(`
            id, origin_country, destination_country,
            total_quote_origincurrency,
            calculation_data, items, status
          `)
          .eq('id', sourceId)
          .single();
        
        if (!quote) {
          console.error('[CART-MANAGER] Quote not found:', sourceId);
          return { unitPrice: 0, currency: 'USD' };
        }
        
        // 🔥 FIX: Use the same calculation method as quote pages
        const { calculateQuoteTotal } = await import('@/utils/quoteCurrencyUtils');
        const { getOriginCurrency } = await import('@/utils/originCurrency');
        const originCurrency = getOriginCurrency(quote.origin_country || 'US');
        
        // 🔥 DEBUG: Deep dive into quote calculation data structure
        console.log('[quote-item-cart] resolveSourceDetails - Quote calculation data analysis:', {
          quoteId: sourceId,
          originCountry: quote.origin_country,
          originCurrency,
          hasCalculationData: !!quote.calculation_data,
          status: quote.status,
          legacyTotal: quote.total_quote_origincurrency,
          legacyFinalTotal: quote.final_total_origincurrency,
          calculationDataStructure: quote.calculation_data ? {
            hasData: !!quote.calculation_data.data,
            hasTotals: !!quote.calculation_data.data?.totals,
            totalValue: quote.calculation_data.data?.totals?.total,
            currency: quote.calculation_data.data?.currency,
            fullTotalsStructure: quote.calculation_data.data?.totals
          } : 'no_calculation_data',
          debugNote: 'Analyzing why calculateQuoteTotal might return 0'
        });

        console.log('[CART-QUOTE-STANDARDIZED] Using standardized calculateQuoteTotal:', {
          quoteId: sourceId,
          originCountry: quote.origin_country,
          originCurrency,
          hasCalculationData: !!quote.calculation_data,
          status: quote.status,
          legacyTotal: quote.total_quote_origincurrency,
          legacyFinalTotal: quote.final_total_origincurrency
        });
        
        try {
          // Calculate using the same method as quote pages (in origin currency)
          const standardizedTotal = await calculateQuoteTotal(quote, originCurrency);
          
          // 🔥 DEBUG: Analyze calculateQuoteTotal result
          console.log('[quote-item-cart] resolveSourceDetails - calculateQuoteTotal result analysis:', {
            quoteId: sourceId,
            inputOriginCurrency: originCurrency,
            calculatedTotal: standardizedTotal,
            isZeroResult: standardizedTotal === 0,
            isValidResult: standardizedTotal > 0,
            expectedFromCalculationData: quote.calculation_data?.data?.totals?.total,
            resultMatchesExpected: standardizedTotal === quote.calculation_data?.data?.totals?.total,
            debugNote: standardizedTotal === 0 ? 
              'PROBLEM: calculateQuoteTotal returned 0 - this is why cart shows no total!' : 
              'SUCCESS: calculateQuoteTotal returned valid amount',
            timestamp: new Date().toISOString()
          });
          
          console.log('[CART-QUOTE-STANDARDIZED] Quote total calculated successfully:', {
            quoteId: sourceId,
            standardizedTotal,
            originCurrency,
            previousMethod: 'final_total_origincurrency fallback',
            newMethod: 'calculateQuoteTotal standardized',
            fixNote: 'Now matches quote page calculation exactly'
          });
          
          return {
            unitPrice: standardizedTotal,
            currency: originCurrency
          };
        } catch (error) {
          console.error('[CART-QUOTE-STANDARDIZED] calculateQuoteTotal failed, using fallback:', error);
          
          // 🔥 ENHANCED FALLBACK: Try to extract directly from calculation_data if available
          let fallbackPrice = 0;
          let fallbackCurrency = originCurrency;
          
          if (quote.calculation_data?.data?.totals?.total) {
            fallbackPrice = quote.calculation_data.data.totals.total;
            fallbackCurrency = quote.calculation_data.data.currency || originCurrency;
            console.log('[quote-item-cart] Using calculation_data direct extraction as fallback:', {
              quoteId: sourceId,
              calculationDataTotal: fallbackPrice,
              calculationDataCurrency: fallbackCurrency,
              reason: 'calculateQuoteTotal function failed but calculation_data exists'
            });
          } else {
            // Final fallback to legacy fields
            fallbackPrice = quote.total_quote_origincurrency || 0;
            console.log('[quote-item-cart] Using legacy fields as final fallback:', {
              quoteId: sourceId,
              fallbackPrice,
              reason: 'No calculation_data available, using legacy quote fields'
            });
          }
          
          console.log('[CART-QUOTE-FALLBACK] Using enhanced fallback method:', {
            quoteId: sourceId,
            fallbackPrice,
            fallbackCurrency,
            error: error instanceof Error ? error.message : 'Unknown error'
          });
          
          return {
            unitPrice: fallbackPrice,
            currency: fallbackCurrency
          };
        }
      }

      case 'product': {
        // AWS Migration: Use AWS Catalog Service with ID filter
        const { awsCatalogService } = await import('@/services/AWSCatalogService');

        // Get all products and filter by ID (since we don't have getProductById yet)
        const result = await awsCatalogService.getProducts({}, { limit: 100 });

        if (result.error || !result.data) {
          console.error('[CART-MANAGER] Failed to fetch products from AWS:', result.error);
          return { unitPrice: 0, currency: 'USD' };
        }

        // Find the specific product by ID
        const product = result.data.find(p => p.id === sourceId);

        if (!product) {
          console.error('[CART-MANAGER] Product not found in AWS:', sourceId);
          return { unitPrice: 0, currency: 'USD' };
        }

        console.log('[CART-MANAGER] AWS Product pricing resolved:', {
          productId: sourceId,
          title: product.title,
          unitPrice: product.price_origin,
          currency: product.origin_currency
        });

        return {
          unitPrice: product.price_origin || 0,
          currency: product.origin_currency || 'USD'
        };
      }

      case 'consolidation': {
        console.log('🚚 [CONSOLIDATION-CART-PRICE] Resolving consolidation pricing:', { sourceId });
        
        const { data: consolidation, error } = await supabase
          .from('consolidation_requests')
          .select('consolidation_data, status')
          .eq('id', sourceId)
          .single();
        
        if (error) {
          console.error('🚚 [CONSOLIDATION-CART-PRICE] ❌ Failed to fetch consolidation data:', { sourceId, error });
          return { unitPrice: 0, currency: 'USD' };
        }
        
        // CRITICAL FIX: Extract shipping cost from delivery_preferences.total_shipping_cost (current active cost)
        // Instead of shipping_preferences.cost (which may be old/outdated)
        const deliveryShippingCost = consolidation?.consolidation_data?.delivery_preferences?.total_shipping_cost;
        const fallbackShippingCost = consolidation?.consolidation_data?.shipping_preferences?.cost;
        const shippingCost = deliveryShippingCost || fallbackShippingCost || 0;
        
        console.log('🚚 [CONSOLIDATION-CART-PRICE] ✅ Consolidation pricing resolved:', {
          sourceId,
          status: consolidation?.status,
          deliveryShippingCost, // Should be 18 USD
          fallbackShippingCost, // Was 37.5 USD (old)
          finalShippingCost: shippingCost,
          fixApplied: !!deliveryShippingCost,
          dataPath: deliveryShippingCost ? 'delivery_preferences.total_shipping_cost' : 'shipping_preferences.cost',
          finalUnitPrice: shippingCost,
          currency: 'USD'
        });
        
        return {
          unitPrice: shippingCost,
          currency: 'USD' // Consolidation shipping costs are typically in USD
        };
      }

      default:
        console.warn('[CART-MANAGER] Unknown source type:', sourceType);
        return { unitPrice: 0, currency: 'USD' };
    }
  }

  /**
   * Private helper: Fetch products from AWS catalog service
   */
  private async fetchProductsFromAWS(productIds: string[]): Promise<{ data: any[] }> {
    try {
      // Import AWS catalog service dynamically to avoid circular dependencies
      const { awsCatalogService } = await import('@/services/AWSCatalogService');

      // Get all products from AWS
      const result = await awsCatalogService.getProducts({}, { limit: 200 });

      if (result.success && result.data) {
        // Filter products by the requested IDs
        const filteredProducts = result.data.filter(product =>
          productIds.includes(product.id)
        );

        // Transform to match expected structure
        const transformedProducts = filteredProducts.map(product => ({
          id: product.id,
          title: product.title,
          brand: product.brand,
          featured_image: product.featured_image,
          origin_currency: product.origin_currency
        }));

        return { data: transformedProducts };
      } else {
        console.warn('[CART-MANAGER] Failed to fetch products from AWS:', result);
        return { data: [] };
      }
    } catch (error) {
      console.error('[CART-MANAGER] Error fetching products from AWS:', error);
      return { data: [] };
    }
  }

  /**
   * Private helper: Validate source exists
   */
  private async validateSourceExists(sourceType: string, sourceId: string): Promise<boolean> {
    switch (sourceType) {
      case 'quote': {
        const { data: quote } = await supabase
          .from('quotes_v2')
          .select('id')
          .eq('id', sourceId)
          .single();
        return !!quote;
      }

      case 'product': {
        // Use AWS catalog service to validate product exists
        const { awsCatalogService } = await import('@/services/AWSCatalogService');
        const result = await awsCatalogService.getProducts({}, { limit: 200 });

        const product = result.success && result.data
          ? result.data.find(p => p.id === sourceId)
          : null;

        return !!product;
      }

      case 'consolidation': {
        const { data: consolidation } = await supabase
          .from('consolidation_requests')
          .select('id, status, actual_shipping_cost')
          .eq('id', sourceId)
          .single();
        
        // Consolidation is valid if it exists and is ready for payment
        const isValid = !!consolidation && consolidation.status === 'ready_for_payment';
        
        if (consolidation && !isValid) {
          console.warn('[CART-CONSOLIDATION-VALIDATION] Consolidation not ready for cart:', {
            sourceId,
            status: consolidation.status,
            hasShippingCost: !!consolidation.actual_shipping_cost
          });
        }
        
        return isValid;
      }

      default:
        return false;
    }
  }

  /**
   * Private helper: Update cart with discount information
   */
  private async updateCartDiscount(cartId: string, discount: AppliedDiscount): Promise<void> {
    const { data: cartItems, error } = await supabase
      .from('cart_items')
      .select('*')
      .eq('session_id', cartId);

    if (error || !cartItems) {
      throw new Error('Failed to load cart items for discount application');
    }

    // Apply discount proportionally to items
    for (const item of cartItems) {
      const discountedPrice = (item.original_amount || 0) * (1 - discount.discount_amount / 100);
      
      await supabase
        .from('cart_items')
        .update({
          discounted_amount: discountedPrice
        })
        .eq('id', item.id);
    }
  }
}

// Export singleton instance
export const unifiedCartManager = UnifiedCartManager.getInstance();